/*
 * Copy shared by every page: navigation, footer and contact details.
 *
 * Source: Tulus website sitemap and content (October 2026), English master.
 * The Malay is written for the site in the brand voice (Tulus brand
 * guidelines, October 2026: natural Malay, no pressure) and still needs a
 * native review before /beta/ is promoted (TODO [Tulus-Platform-Site]).
 * EN and MS share one shape, so a missing string is a type error.
 */
import type { Language } from "../components/useLanguage";

export const APP_URL = "https://app.tulus.my/";
export const APP_STORE = "https://apps.apple.com/my/app/tulus-zakat-sedekah-online/id1529924310";
export const PLAY_STORE = "https://play.google.com/store/apps/details?id=my.snapnpay.tulus.android";

// Tulus's own social profiles: the footer icons and the Organization data (sameAs).
export const SOCIAL = {
  facebook: "https://www.facebook.com/tulusdigital/",
  x: "https://twitter.com/tulusdigital",
  instagram: "https://www.instagram.com/tulusdigital/",
  linkedin: "https://www.linkedin.com/company/tulusdigital",
};

export const CONTACT = {
  email: "tulus@tulus.my",
  phone: "+603 5567 0103",
  phoneHref: "tel:+60355670103",
  company: "Tulus Digital Sdn Bhd",
  // Company number, new format then old, as SSM and the terms page write it.
  registration: "201101002165 (930302-A)",
  // Section U1 after the business park and before the street; "1", not "No. 1".
  addressLines: [
    "Unit 3-03-07, UBP Tower 3, UOA Business Park, Section U1",
    "1, Jalan Pengaturcara U1/51a",
    "40150 Shah Alam, Selangor, Malaysia",
  ],
};

export type NavItem = { label: string; path: string };

const EN = {
  nav: [
    { label: "Platform", path: "/platform" },
    { label: "Solutions", path: "/penyelesaian" },
    { label: "Giving & Services", path: "/perkhidmatan" },
    { label: "Partnerships", path: "/kerjasama" },
    { label: "Governance", path: "/tadbir-urus" },
    { label: "About", path: "/tentang-kami" },
  ] as NavItem[],
  talk: "Talk to Tulus",
  app: "Explore Tulus",
  home: "Tulus, home",
  menuOpen: "Open menu",
  menuClose: "Close menu",
  mainNav: "Main navigation",
  language: "Language",
  footer: {
    category: "Digital Islamic Social Finance Platform.",
    promise: "Connecting giving to community impact.",
    summary:
      "Tulus Digital Sdn Bhd is a Malaysian technology company bringing a platform approach to Islamic social finance through digital services and institutional collaboration.",
    explore: "Explore",
    contributors: "For contributors",
    printReceipt: "Print a receipt",
    contact: "Contact",
    location: "Shah Alam, Selangor, Malaysia",
    links: [
      { label: "Contact", path: "/hubungi-kami" },
      { label: "Privacy Notice", path: "/privacy-policy" },
      { label: "Terms of Use", path: "/terma-dan-syarat" },
    ] as NavItem[],
    rights: "All rights reserved.",
    // The group mark, as on actsports.my (2026-10-18): acture.my lists Tulus Digital as a
    // featured group company (acture.my/portfolio/tulus/).
    acture: { before: "An", after: "company", label: "An Acture company, at acture.my (opens in a new tab)" },
    appStore: "Download on the App Store",
    playStore: "Get it on Google Play",
  },
};

export type CommonCopy = typeof EN;

const MS: CommonCopy = {
  nav: [
    { label: "Platform", path: "/platform" },
    { label: "Penyelesaian", path: "/penyelesaian" },
    { label: "Perkhidmatan", path: "/perkhidmatan" },
    { label: "Kerjasama", path: "/kerjasama" },
    { label: "Tadbir Urus", path: "/tadbir-urus" },
    { label: "Tentang Kami", path: "/tentang-kami" },
  ],
  talk: "Hubungi Tulus",
  app: "Terokai Tulus",
  home: "Tulus, laman utama",
  menuOpen: "Buka menu",
  menuClose: "Tutup menu",
  mainNav: "Navigasi utama",
  language: "Bahasa",
  footer: {
    category: "Platform Kewangan Sosial Islam Digital.",
    promise: "Menghubungkan sumbangan dengan impak kepada komuniti.",
    summary:
      "Tulus Digital Sdn Bhd ialah syarikat teknologi Malaysia yang membawa pendekatan platform kepada kewangan sosial Islam melalui perkhidmatan digital dan kerjasama dengan institusi.",
    explore: "Terokai",
    contributors: "Untuk penyumbang",
    printReceipt: "Cetak resit",
    contact: "Hubungi",
    location: "Shah Alam, Selangor, Malaysia",
    links: [
      { label: "Hubungi Kami", path: "/hubungi-kami" },
      { label: "Notis Privasi", path: "/privacy-policy" },
      { label: "Terma Penggunaan", path: "/terma-dan-syarat" },
    ],
    rights: "Hakcipta Terpelihara.",
    acture: { before: "Syarikat kumpulan", after: "", label: "Syarikat kumpulan Acture, di acture.my (dibuka di tab baharu)" },
    appStore: "Muat turun di App Store",
    playStore: "Dapatkan di Google Play",
  },
};

export const COMMON: Record<Language, CommonCopy> = { en: EN, ms: MS };
